import {
  CircularProgress,
  IconButton as MuiIconButton,
  InputAdornment,
  type SxProps,
  type Theme,
} from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { useDebouncedValue } from "@src/appV2/lib/utils/useDebouncedValue";
import { useCallback, useEffect, useRef, useState } from "react";

import { Icon } from "../Icon";
import { TextInput } from "../TextInput";
import { useTu } from "@src/appV2/i18n/useTu";

const DEFAULT_DEBOUNCE_MS = 300;

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  debounceMs?: number;
  isLoading?: boolean;
  showClearButton?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export function SearchInput(props: SearchInputProps) {
  const { tu } = useTu("common");
  const {
    value,
    onChange,
    placeholder = "Search...",
    autoFocus = false,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    isLoading = false,
    showClearButton = true,
    disabled = false,
    sx,
  } = props;

  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousExternalValueRef = useRef(value);
  const isSyncingExternalValueRef = useRef(false);
  const [debouncedValue] = useDebouncedValue(internalValue, debounceMs);

  useEffect(() => {
    if (value !== previousExternalValueRef.current) {
      previousExternalValueRef.current = value;
      isSyncingExternalValueRef.current = true;
      setInternalValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (isSyncingExternalValueRef.current) {
      if (debouncedValue === value) {
        isSyncingExternalValueRef.current = false;
      }

      return;
    }

    // Only trigger onChange when debounced value differs from external value
    // AND matches the current internal value (prevents overwriting direct onChange calls like clear)
    if (debouncedValue !== value && debouncedValue === internalValue) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange, value, internalValue]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(event.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setInternalValue("");
    onChange("");
    inputRef.current?.focus();
  }, [onChange]);

  const showClear = showClearButton && internalValue.length > 0 && !isLoading;

  return (
    <TextInput
      fullWidth
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      disabled={disabled}
      inputRef={inputRef}
      placeholder={placeholder}
      size="small"
      value={internalValue}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Icon
              size="medium"
              sx={(theme) => ({
                color: theme.palette.text.disabled,
              })}
              type="magnifying-glass"
            />
          </InputAdornment>
        ),
        endAdornment: (isLoading || showClear) && (
          <InputAdornment position="end">
            {isLoading ? (
              <CircularProgress size={14} />
            ) : (
              showClear && (
                <MuiIconButton
                  aria-label={tu("Clear search")}
                  edge="end"
                  size="small"
                  onClick={handleClear}
                >
                  <Icon
                    size="small"
                    sx={(theme) => ({
                      color: theme.palette.text.disabled,
                    })}
                    type="close"
                  />
                </MuiIconButton>
              )
            )}
          </InputAdornment>
        ),
      }}
      sx={mergeSxProps(
        (theme) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.background.standout ?? theme.palette.background.paper,
          },
          "& .MuiOutlinedInput-input": {
            padding: theme.spacing(3),
          },
        }),
        sx
      )}
      onChange={handleChange}
    />
  );
}
